import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faFileChartLine, faStar, faTasks,
} from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import {
  Box, Button, Columns, PageHeader, Tools,
} from '@domain-group/suit-theme'

import PageContext, { DefaultContext } from '@contexts/pageContext'

import useTaskList from '@hooks/useTaskList'

import PageFilterModule from '@components/PageFilterModule'

import TaskListModule from '../TaskListModule'

import type { PageContextPayload } from '../../index'

const getActiveTabBarKey = (isAdHoc: boolean, isCompleted: boolean) => {
  if (isAdHoc) return 'adHoc'
  if (isCompleted) return 'completed'
  return 'current'
}

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'owner_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_due_date',
      label: 'Start Date',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_due_date',
      label: 'End Date',
    },
  ]
}

interface PageContentProps {
  pageFilters: object,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { adHoc, completed } = parsePermittedQueryParams(getQueryParamsFromHash(), ['adHoc', 'completed'])
  const isAdHoc = adHoc === 'true'
  const isCompleted = completed === 'true'

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateTemplateGroupModal,
      showGenerateReportModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const mergedPageFilters = {
    ...pageFilters,
    ad_hoc: isAdHoc,
  }

  const taskListPayload = useTaskList({ ad_hoc: isAdHoc, owner_type: 'Brand' })
  const {
    callbacks: { createOrEditTaskList },
  } = taskListPayload

  return (
    <>
      <Helmet>
        <title>Projects | Template Manager</title>
      </Helmet>

      <PageHeader
        actionContent={
          isAdHoc ? (
            <Button buttonStyle="primaryEdit" onClick={() => createOrEditTaskList()} size="medium">
              Create Ad Hoc Project
            </Button>
          ) : (
            <Button buttonStyle="primaryCreate" onClick={() => showCreateTemplateGroupModal()} size="medium">
              Create Project
            </Button>
          )
        }
        activeTabBarItemKey={getActiveTabBarKey(isAdHoc, isCompleted)}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: '#/projects?adHoc=false',
            icon: faTasks,
            key: 'current',
            title: 'Current',
          },
          {
            href: '#/projects?adHoc=true',
            icon: faStar,
            key: 'adHoc',
            title: 'Ad Hoc',
          },
          {
            href: '#/projects?completed=true',
            icon: faCheck,
            key: 'completed',
            title: 'Completed',
          },
        ]}
        title="Projects"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {isCompleted && (
              <TaskListModule pageFilters={mergedPageFilters} progressStatus="completed" title="Completed" />
            )}

            {!isCompleted && (
              <>
                <TaskListModule pageFilters={mergedPageFilters} progressStatus="in_progress" title="In Progress" />
                <TaskListModule pageFilters={mergedPageFilters} progressStatus="not_started" title="Not Started" />
              </>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                marginBottom="medium"
                onClick={() => createOrEditTaskList({ createUsingUsageReport: true })}
                size="medium"
              >
                Create from Usage Report
              </Button>

              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faFileChartLine} />}
                marginBottom="medium"
                onClick={() => showGenerateReportModal()}
                size="medium"
              >
                Generate Report
              </Button>
            </Tools>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={mergedPageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
